import { Art } from "./Art";
import { LogoMarki } from "./LogoMarki";
import { t } from "../i18n";
import { Icons } from "../lib/icons";
import type { Motyw } from "./Motyw";
import type { Branding, Locale, Stage } from "../types";

/**
 * Lewa kolumna na szerokich ekranach: kolor i kontekst po jednej stronie, robota
 * po drugiej. Niesie to, czego nie widać w samym pytaniu — na jakim etapie jesteśmy
 * i ile zostało. Na wąskim ekranie w ogóle się nie renderuje (CSS), bo tam liczy
 * się każdy piksel wysokości.
 */
export function Aside({
  stages,
  progress,
  locale,
  motyw,
  branding,
}: {
  stages: Stage[];
  progress: number;
  locale: Locale;
  /** Karta logo zabrania czerwonego sygnetu na ciemnym tle — stąd wariant. */
  motyw: Motyw;
  /** Szablon marki: logo i powitanie. Bez niego — for apps i teksty z i18n. */
  branding?: Branding | null;
}) {
  const powitanie = branding?.texts.welcome;
  return (
    <aside className="aside">
      <div className="aside__brand">
        <LogoMarki branding={branding} motyw={motyw} miejsce="kolumna" />
      </div>

      <div>
        <h1 className="aside__title">{powitanie?.title ?? t("asideTitle", locale)}</h1>
        <p className="aside__lead">{powitanie?.text ?? t("asideLead", locale)}</p>
      </div>

      {stages.length > 0 && (
        // Kaskada z tokens.css: etapy wchodzą jeden po drugim przy montażu
        // listy; późniejsze zmiany stanów nie przemontowują elementów,
        // więc nic nie mryga przy każdej odpowiedzi.
        <ol className="stages kaskada">
          {stages.map((stage, index) => (
            <li
              key={stage.id}
              className={`stage stage--${stage.state}`}
              style={{ "--i": index } as React.CSSProperties}
            >
              <span className="stage__bullet" aria-hidden="true">
                {stage.state === "done" ? <Icons.check size={13} /> : null}
              </span>
              {stage.label}
            </li>
          ))}
        </ol>
      )}

      <span className="aside__spacer" />

      {/* Ilustracja zmienia się razem z etapem — widać, w którym miejscu procesu
          jesteśmy. Render ma zaprojektowane tło (diagonala ciepła biel → czerń),
          więc leży na całej powierzchni kafla jak okładka — bez wycinania
          i bez filtrów, przez co działa w obu motywach. */}
      <div className="aside__stageart">
        <Art name={stages.find((s) => s.state === "current")?.id ?? "start"} size={320} />
      </div>

      <div className="aside__foot" aria-live="polite">
        {/* Pasek jest wyłącznie wizualny (aria-hidden) — treść niesie procent
            obok; ten sam postęp, który na telefonie pokazują segmenty. */}
        <span className="aside__meter" aria-hidden="true">
          <span style={{ "--p": progress / 100 } as React.CSSProperties} />
        </span>
        <span className="aside__footrow">
          {/* Klucz z wartości — procent wjeżdża jak licznik przy każdej zmianie. */}
          <span key={progress} className="aside__pct wjazd">
            {progress}%
          </span>{" "}
          — {t("asideProgress", locale)}
        </span>
      </div>
    </aside>
  );
}
